import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Wine, Phone, Calendar, Users, Clock, MapPin, X } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'

type Status = 'new' | 'contacted' | 'booked' | 'completed' | 'declined'

interface CateringRequest {
  id: string
  user_id: string | null
  user_name: string | null
  user_phone: string | null
  contact_name: string
  contact_phone: string
  event_date: string
  guest_count: number
  hours: number
  bartender_count: number
  drink_package: 'soft' | 'beer_wine' | 'standard' | 'premium'
  location: string
  notes: string | null
  estimate_fils: number
  status: Status
  admin_notes: string | null
  created_at: string
  updated_at: string
}

const PACKAGE_LABELS: Record<string, string> = {
  soft:      'Soft drinks only',
  beer_wine: 'Beer & wine',
  standard:  'Standard spirits',
  premium:   'Premium open bar',
}

const STATUS_COLORS: Record<Status, string> = {
  new:       'bg-amber-500/15  text-amber-400  border-amber-500/30',
  contacted: 'bg-blue-500/15   text-blue-400   border-blue-500/30',
  booked:    'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  completed: 'bg-gray-500/15   text-gray-400   border-gray-500/30',
  declined:  'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
}

const STATUS_OPTIONS: Status[] = ['new', 'contacted', 'booked', 'completed', 'declined']

export default function Catering() {
  const [filter, setFilter] = useState<Status | 'all'>('new')
  const [editing, setEditing] = useState<CateringRequest | null>(null)

  const { data, isLoading } = useQuery<{ requests: CateringRequest[] }>({
    queryKey: ['admin-catering', filter],
    queryFn: () => {
      const q = filter === 'all' ? '' : `?status=${filter}`
      return api.get(`/catering${q}`).then(r => r.data)
    },
    refetchInterval: 60_000,
  })

  const requests = data?.requests ?? []

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Wine size={24} className="text-[#C41E3A]" />
        <h1 className="text-2xl font-bold text-white">Catering Requests</h1>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {(['new', 'contacted', 'booked', 'completed', 'declined', 'all'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border transition ${
              filter === s
                ? 'bg-[#C41E3A] text-white border-[#C41E3A]'
                : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-gray-400 text-sm">Loading…</p>}

      {!isLoading && requests.length === 0 && (
        <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-8 text-center">
          <Wine size={32} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400 text-sm">No catering requests in this view.</p>
        </div>
      )}

      <div className="space-y-3">
        {requests.map(r => (
          <button
            key={r.id}
            onClick={() => setEditing(r)}
            className="w-full text-left bg-white/[0.02] hover:bg-white/[0.04] border border-[#2e2e2e] rounded-lg p-4 transition"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold">{r.contact_name}</h3>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${STATUS_COLORS[r.status]}`}>
                    {r.status}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Phone size={11} /> {r.contact_phone}
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white">{formatCurrency(r.estimate_fils)}</p>
                <p className="text-[10px] text-gray-500">estimate</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-300">
              <div className="flex items-center gap-1.5"><Calendar size={11} className="text-gray-500" /> {r.event_date}</div>
              <div className="flex items-center gap-1.5"><Users size={11} className="text-gray-500" /> {r.guest_count} guests</div>
              <div className="flex items-center gap-1.5"><Clock size={11} className="text-gray-500" /> {r.hours} hrs · {r.bartender_count} bartenders</div>
              <div className="flex items-center gap-1.5 truncate"><MapPin size={11} className="text-gray-500" /> {r.location}</div>
            </div>

            <div className="mt-2 text-xs text-gray-500">
              {PACKAGE_LABELS[r.drink_package]} · submitted {formatDateTime(r.created_at)}
            </div>
          </button>
        ))}
      </div>

      {editing && (
        <DetailModal request={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  )
}

function DetailModal({ request, onClose }: { request: CateringRequest; onClose: () => void }) {
  const [status, setStatus] = useState<Status>(request.status)
  const [notes, setNotes] = useState(request.admin_notes ?? '')
  const qc = useQueryClient()

  const update = useMutation({
    mutationFn: (payload: { status?: Status; admin_notes?: string }) =>
      api.patch(`/catering/${request.id}`, payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-catering'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[#2e2e2e] flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Catering Request</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5 text-sm">
          <Field label="Contact">{request.contact_name} · {request.contact_phone}</Field>
          {request.user_name && (
            <Field label="App user">{request.user_name} · {request.user_phone}</Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Event date">{request.event_date}</Field>
            <Field label="Location">{request.location}</Field>
            <Field label="Guests">{request.guest_count}</Field>
            <Field label="Hours">{request.hours}</Field>
            <Field label="Bartenders">{request.bartender_count}</Field>
            <Field label="Package">{PACKAGE_LABELS[request.drink_package]}</Field>
          </div>
          {request.notes && <Field label="Customer notes">{request.notes}</Field>}

          <Field label="Estimate">
            <span className="text-lg font-bold text-white">{formatCurrency(request.estimate_fils)}</span>
          </Field>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as Status)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
            >
              {STATUS_OPTIONS.map(s => <option key={s} value={s} className="bg-[#1a1a1a] capitalize">{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Internal notes</label>
            <textarea
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Phone discussion, deposit details, etc."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => update.mutate({ status, admin_notes: notes })}
              disabled={update.isPending}
              className="flex-1 px-4 py-2 rounded-lg bg-[#C41E3A] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">{label}</p>
      <div className="text-white">{children}</div>
    </div>
  )
}
